import React, {useEffect, useState} from "react";
import DatePicker from "../../atoms/datePicker/datePicker";
import TimePicker from "../../atoms/timePicker/timePicker";
import {DateObservable} from "../../../stores/observables/dateObservable";
import {observer} from "mobx-react-lite";
import {Label} from "reactstrap";
import "./dateTimePicker.sass";
import {isSameDay} from "date-fns";
import * as Popper from "@popperjs/core";

export interface IDateTimePickerProps {
  value: DateObservable;
  label?: string;
  datePickerErrorMessage?: string;
  dateFormat?: string;
  disabled?: boolean;
  placeholder?: string;
  openToDate?: Date | null;
  helpText?: string;
  timeZone: string;
  showTimeSelect?: boolean;
  preventManualEntry?: boolean;
  enabledDates?: Date[];
  onChange?: () => void;
  showTimeSelectOnly?: boolean;
  disableMinTime?: boolean;
  isRequired?: boolean;
  interval?: number;
  timePickerErrorMessage?: string;
  dateTimePickerErrorMessage?: string;
  position?: Popper.Placement;
}
const DateTimePicker: React.FC<IDateTimePickerProps> = ({
  value,
  label,
  datePickerErrorMessage,
  dateFormat,
  disabled,
  placeholder,
  openToDate,
  helpText,
  timeZone,
  showTimeSelect = true,
  preventManualEntry,
  enabledDates,
  onChange,
  showTimeSelectOnly = false,
  disableMinTime,
  isRequired,
  interval,
  timePickerErrorMessage,
  dateTimePickerErrorMessage,
  position = "auto"
}) => {
  const [timePickerLimit, setTimePickerLimit] = useState({
    minMinutes: 0,
    minHours: 0,
    maxMinutes: 59,
    maxHours: 23
  });

  const minTime = disableMinTime
    ? new Date(new Date().setHours(0, 0, 0, 0))
    : value.calculateMinDate(timeZone);

  useEffect(() => {
    if (value.date) {
      if (value.minDate && isSameDay(value.minDate, value.date)) {
        setTimePickerLimit({
          ...timePickerLimit,
          minMinutes: value.minDate.getMinutes(),
          minHours: value.minDate.getHours()
        });
      } else if (value.maxDate && isSameDay(value.maxDate, value.date)) {
        setTimePickerLimit({
          ...timePickerLimit,
          maxMinutes: value.maxDate.getMinutes(),
          maxHours: value.maxDate.getHours()
        });
      } else {
        setTimePickerLimit({
          minMinutes: 0,
          minHours: 0,
          maxMinutes: 59,
          maxHours: 23
        });
      }
    }
  }, [value.date]);

  return (
    <div className="date-time-picker">
      <div className="label">
        {label && (
          <Label for={label}>
            {label}
            {isRequired && "*"}
          </Label>
        )}
        {helpText && <Label className="help-text">{helpText}</Label>}
      </div>
      {!showTimeSelectOnly && (
        <DatePicker
          value={value}
          validationMessage={datePickerErrorMessage}
          dateFormat={dateFormat}
          disabled={disabled}
          placeholder={placeholder}
          openToDate={openToDate}
          timeZone={timeZone}
          preventManualEntry={preventManualEntry}
          enabledDates={enabledDates}
          onChange={onChange}
          disableMinTime={disableMinTime}
          isRequired={isRequired}
          minTime={minTime}
          position={position}
        />
      )}
      {showTimeSelect && (
        <TimePicker
          interval={interval}
          timePickerErrorMessage={timePickerErrorMessage}
          date={value}
          timeZone={timeZone}
          isDisabled={!value.date || disabled}
          minTime={minTime}
          position={position}
        />
      )}
      {dateTimePickerErrorMessage &&
        value.isDirty &&
        !value.isValid &&
        value.isTimeValid && (
          <div
            className={"error-message"}
            data-testid="date-time-picker-error-message"
          >
            {dateTimePickerErrorMessage}
          </div>
        )}
    </div>
  );
};

export default observer(DateTimePicker);
